/**
 * Componente: RecentApplicationsTable
 * Tabela com aplicações recentes de médicos
 */

import React from 'react';
import { CheckCircle2, Clock } from 'lucide-react';

export interface DoctorApplication {
  id: string;
  name: string;
  specialty: string;
  appliedDate: string;
  licenseId: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface RecentApplicationsTableProps {
  applications?: DoctorApplication[];
  isLoading?: boolean;
}

const statusConfig = {
  pending: {
    badge: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-200 border-amber-200 dark:border-amber-800',
    label: 'Pendente',
    icon: Clock,
  },
  approved: {
    badge: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-200 border-green-200 dark:border-green-800',
    label: 'Aprovado',
    icon: CheckCircle2,
  },
  rejected: {
    badge: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 border-red-200 dark:border-red-800',
    label: 'Rejeitado',
    icon: CheckCircle2,
  },
};

export function RecentApplicationsTable({
  applications = [],
  isLoading = false,
}: RecentApplicationsTableProps) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Médicos Recentes
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Últimos cadastros pendentes de aprovação
          </p>
        </div>
        <button className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium">
          Ver todos
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Carregando médicos...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500">Nenhum médico pendente de aprovação no momento.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Médico
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Especialidade
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Data de Cadastro
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  CRM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
              {applications.map((app) => {
                const statusInfo = statusConfig[app.status] || statusConfig.pending;
                const StatusIcon = statusInfo.icon;
                return (
                  <tr
                    key={app.id}
                    className="hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {app.name}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {app.specialty}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {app.appliedDate}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {app.licenseId}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.badge}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
